import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChangeProposal,
  EntityType,
  PendingEntryStatus,
  WorkflowQuery,
  WorkflowResponse,
} from '../../types/workflow';
import { workflowService } from '../../services/workflowService';
import { asNumber, asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { QuickDecision, ReviewQueue } from './ReviewQueue';
import { getEntityTypeLabel, proposalName } from './workflowFormat';

const DEFAULT_LIMIT = 10;

/* One codec per parameter, made once: the URL state reads them on every render */
const STATUS = asOneOf<PendingEntryStatus | 'all'>([...Object.values(PendingEntryStatus), 'all']);
const TYPE = asOneOf<EntityType | ''>(Object.values(EntityType));
const PAGE = asNumber(1);
const LIMIT = asNumber(1, 100);

interface ChangeProposalsListProps {
  onSelectProposal?: (proposal: ChangeProposal) => void;
  showFilters?: boolean;
  assignedToMe?: boolean;
  /** Called after a proposal is approved or rejected from the list */
  onReviewed?: () => void;
  /** Names this queue's URL parameters; two queues share one address bar */
  scope?: string;
}

const getChangesCount = (proposal: ChangeProposal) => {
  if (!proposal.proposedChanges || typeof proposal.proposedChanges !== 'object') {
    return 0;
  }
  return Object.keys(proposal.proposedChanges).length;
};

export const ChangeProposalsList: React.FC<ChangeProposalsListProps> = ({
  onSelectProposal,
  showFilters = true,
  assignedToMe = false,
  onReviewed,
  scope = 'changes',
}) => {
  const [proposals, setProposals] = useState<ChangeProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  // The queue someone is looking at is in the address bar, under names of its
  // own so that two queues on one screen do not overwrite each other
  const [status] = useUrlState<PendingEntryStatus | 'all'>(`${scope}Status`, PendingEntryStatus.PENDING, STATUS);
  const [entityType] = useUrlState<EntityType | ''>(`${scope}Type`, '', TYPE);
  const [page, setPage] = useUrlState(`${scope}Page`, 1, PAGE);
  const [limit] = useUrlState(`${scope}Limit`, DEFAULT_LIMIT, LIMIT);

  const filters = useMemo<WorkflowQuery>(
    () => ({
      status: status === 'all' ? undefined : status,
      entityType: entityType || undefined,
      page,
      limit,
    }),
    [status, entityType, page, limit],
  );

  const loadProposals = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);

      let response: WorkflowResponse<ChangeProposal>;

      if (assignedToMe) {
        const myAssignments = await workflowService.getMyAssignments(filters);
        response = myAssignments.changeProposals;
      } else {
        response = await workflowService.getChangeProposals(filters);
      }

      setProposals(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load change proposals');
    } finally {
      setLoading(false);
    }
  }, [filters, assignedToMe]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  /* Changing a filter starts again at page one — one change, not two */
  const write = useUrlWriter();
  const handleFilterChange = (newFilters: Partial<WorkflowQuery>) => {
    const next = { ...filters, ...newFilters };
    write({
      // No status at all means every status, which is not the default
      [`${scope}Status`]: next.status === undefined ? 'all' : next.status === PendingEntryStatus.PENDING ? null : next.status,
      [`${scope}Type`]: next.entityType ?? null,
      [`${scope}Limit`]: next.limit && next.limit !== DEFAULT_LIMIT ? String(next.limit) : null,
      [`${scope}Page`]: null,
    });
  };

  const handleDecide = async (id: string, status: QuickDecision) => {
    setActing(`${id}:${status}`);
    setActionError(null);
    try {
      await workflowService.reviewChangeProposal(id, { status });
      await loadProposals(true);
      onReviewed?.();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to submit review'));
    } finally {
      setActing(null);
    }
  };

  return (
    <ReviewQueue
      rows={proposals.map(proposal => {
        const changes = getChangesCount(proposal);
        return {
          id: proposal.id,
          name: proposalName(proposal),
          typeLabel: getEntityTypeLabel(proposal.entityType),
          entityType: proposal.entityType,
          extra: `${changes} field${changes !== 1 ? 's' : ''}`,
          by: proposal.proposer?.email,
          at: proposal.proposedAt,
          assignee: proposal.assignee?.email,
          status: proposal.status,
        };
      })}
      loading={loading}
      error={error}
      onRetry={() => loadProposals()}
      showFilters={showFilters}
      filters={filters}
      onFilterChange={handleFilterChange}
      pagination={pagination}
      onPageChange={setPage}
      onOpen={id => {
        const proposal = proposals.find(p => p.id === id);
        if (proposal) onSelectProposal?.(proposal);
      }}
      onDecide={handleDecide}
      acting={acting}
      actionError={actionError}
      emptyLabel={filters.status === PendingEntryStatus.PENDING ? 'Nothing pending' : 'No proposals'}
      byLabel="Proposed by"
    />
  );
};
